/**
 * Inline SVG icons used across the status page. Pure, dependency-free
 * presentational components — extracted from `App.tsx` so the markup is
 * reusable by sibling components without importing from the view file.
 */
import clsx from 'clsx';

export const ChevronRight = (p: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={p.className}
  >
    <path d="M6 4l4 4-4 4" />
  </svg>
);

export const ChevronDown = (p: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={p.className}
  >
    <path d="M4 6l4 4 4-4" />
  </svg>
);

export const Check = (p: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={p.className}
  >
    <path d="M3 8.5l3 3 7-8" />
  </svg>
);

export const SearchIcon = (p: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={p.className}
  >
    <circle cx={7} cy={7} r={4.5} />
    <path d="M10.5 10.5L14 14" />
  </svg>
);

export function TelegramGlyph(p: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className={clsx('h-4 w-4', p.className)}
    >
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  );
}

/** toncenter.com external-link arrow (16px, `currentColor`). */
export const ExternalArrow = (p: { className?: string }) => (
  <svg
    aria-hidden
    focusable="false"
    viewBox="0 0 16 16"
    className={clsx('h-4 w-4 flex-none', p.className)}
  >
    <path
      fill="currentColor"
      d="M3.52381 12.4833C3.65059 12.6064 3.82105 12.6741 3.99775 12.6716C4.17445 12.6691 4.34292 12.5966 4.46616 12.4699L11.342 5.59403L11.342 10.5843C11.3402 10.673 11.3561 10.7611 11.3888 10.8436C11.4215 10.926 11.4703 11.0011 11.5324 11.0645C11.5945 11.1278 11.6686 11.1782 11.7504 11.2125C11.8321 11.2469 11.9199 11.2646 12.0086 11.2646C12.0973 11.2646 12.1851 11.2469 12.2669 11.2125C12.3487 11.1782 12.4228 11.1278 12.4849 11.0645C12.5469 11.0011 12.5957 10.926 12.6284 10.8436C12.6611 10.7611 12.677 10.673 12.6752 10.5843L12.6752 3.98463C12.6752 3.80785 12.605 3.63832 12.4799 3.51331C12.3549 3.38831 12.1854 3.31807 12.0086 3.31803L5.40897 3.31803C5.32029 3.31623 5.23214 3.33214 5.14969 3.36483C5.06723 3.39751 4.99213 3.44633 4.92877 3.5084C4.86542 3.57048 4.81509 3.64458 4.78073 3.72635C4.74637 3.80812 4.72867 3.89593 4.72867 3.98463C4.72867 4.07333 4.74637 4.16114 4.78073 4.24291C4.81509 4.32468 4.86542 4.39878 4.92877 4.46086C4.99213 4.52293 5.06723 4.57175 5.14969 4.60443C5.23214 4.63712 5.32029 4.65303 5.40897 4.65123L10.3992 4.65122L3.52335 11.5271C3.45946 11.5893 3.40868 11.6636 3.37403 11.7458C3.33937 11.8279 3.32154 11.9161 3.32158 12.0053C3.32162 12.0944 3.33954 12.1827 3.37428 12.2648C3.40901 12.3469 3.45986 12.4212 3.52381 12.4833Z"
    />
  </svg>
);

/**
 * The TON Center wordmark exactly as toncenter.com's header draws it
 * (136×28): the lettering takes `currentColor`, the round mark is the
 * accent.
 */
export const TonCenterLogo = (p: { className?: string }) => (
  <svg
    aria-hidden
    focusable="false"
    viewBox="0 0 136 28"
    className={clsx('block h-7 w-auto', p.className)}
  >
    <g transform="translate(32.4961 7.82617) scale(0.96178896)" fill="currentColor">
      <path d="M4.35998 14.2282H7.51304V3.23189H11.8728V0.236481H5.98072e-06V3.23189H4.35998V14.2282Z" />
      <path d="M19.4507 14.4647C23.4314 14.4647 26.683 11.2131 26.683 7.23234C26.683 3.2516 23.4314 9.53674e-07 19.4507 9.53674e-07C15.4502 9.53674e-07 12.2183 3.2516 12.2183 7.23234C12.2183 11.2131 15.4502 14.4647 19.4507 14.4647ZM19.4507 11.4298C17.1647 11.4298 15.3714 9.4986 15.3714 7.23234C15.3714 4.96607 17.1647 3.03482 19.4507 3.03482C21.7169 3.03482 23.5102 4.96607 23.5102 7.23234C23.5102 9.4986 21.7169 11.4298 19.4507 11.4298Z" />
      <path d="M41.0719 14.2282V0.236481H37.9189V8.86799L31.1201 0.236481H28.4202V14.2282H31.5536V5.57698L38.3721 14.2282H41.0719Z" />
      <path d="M101.681 14.4003V4.5435H103.849V6.29801C104.224 5.17433 105.328 4.44493 106.452 4.44493C106.728 4.44493 107.004 4.46464 107.299 4.52378V6.75142C106.925 6.63314 106.609 6.574 106.215 6.574C104.973 6.574 103.849 7.61882 103.849 9.59017V14.4003H101.681Z" />
      <path d="M99.6316 11.3053C99.0796 13.1583 97.3054 14.5974 94.8017 14.5974C91.8644 14.5974 89.7551 12.4289 89.7551 9.47189C89.7551 6.55428 91.7856 4.34636 94.8215 4.34636C97.8376 4.34636 99.947 6.65285 99.7498 10.0042H91.9038C91.9827 11.5024 93.1261 12.6852 94.8806 12.6852C95.9648 12.6852 96.9111 12.2515 97.3645 11.3053H99.6316ZM94.7623 6.16001C93.4021 6.16001 92.2784 6.94855 91.9827 8.4665H97.4236C97.0885 6.8697 95.9648 6.16001 94.7623 6.16001Z" />
      <path d="M88.8182 14.282C88.3845 14.42 87.872 14.4989 87.2017 14.4989C85.3092 14.4989 83.8307 13.454 83.8307 11.2264V6.47543H81.7805V4.5435H83.8307V1.80331H85.9992V4.5435H88.8182V6.47543H85.9992V10.8716C85.9992 12.0938 86.5314 12.5472 87.6551 12.5472C88.0691 12.5472 88.5028 12.4684 88.8182 12.3501V14.282Z" />
      <path d="M71.6301 14.4003V4.5435H73.7986V5.88402C74.39 4.89834 75.5136 4.34636 76.795 4.34636C79.0818 4.34636 80.5603 5.96287 80.5603 8.4665V14.4003H78.3918V8.82134C78.3918 7.26397 77.6033 6.31772 76.3416 6.31772C74.9025 6.31772 73.7986 7.44139 73.7986 9.55074V14.4003H71.6301Z" />
      <path d="M69.5808 11.3053C69.0288 13.1583 67.2546 14.5974 64.751 14.5974C61.8136 14.5974 59.7043 12.4289 59.7043 9.47189C59.7043 6.55428 61.7348 4.34636 64.7707 4.34636C67.7869 4.34636 69.8962 6.65285 69.6991 10.0042H61.8531C61.9319 11.5024 63.0753 12.6852 64.8298 12.6852C65.9141 12.6852 66.8603 12.2515 67.3137 11.3053H69.5808ZM64.7115 6.16001C63.3513 6.16001 62.2276 6.94855 61.9319 8.4665H67.3729C67.0377 6.8697 65.9141 6.16001 64.7115 6.16001Z" />
      <path d="M44.5812 7.40197C44.5812 3.32126 47.7157 0.167084 51.8753 0.167084C55.2463 0.167084 57.947 2.35529 58.6173 5.09548H56.1334C55.542 3.49868 53.8466 2.39472 51.8753 2.39472C48.9577 2.39472 46.9074 4.58293 46.9074 7.40197C46.9074 10.221 48.9577 12.4092 51.8753 12.4092C53.8466 12.4092 55.542 11.3053 56.1334 9.70846H58.6173C57.947 12.4486 55.2463 14.6368 51.8753 14.6368C47.7157 14.6368 44.5812 11.4827 44.5812 7.40197Z" />
    </g>
    <path
      fill="#008def"
      d="M14.0012 0C21.7338 0 28.0024 6.2686 28.0024 14.0012C28.0024 21.7339 21.7338 28.0024 14.0012 28.0024C6.26854 28.0024 2.17955e-05 21.7338 0 14.0012C0 6.26861 6.26853 1.89016e-05 14.0012 0Z"
    />
    <g transform="scale(0.99457294)">
      <rect fill="none" height="11.187" stroke="#fff" strokeWidth="2.065" width="11.187" x="8.4925" y="8.6025" />
      <rect fill="#008def" height="13.663" width="3.972" x="12.09" y="7.36" />
      <rect fill="#008def" height="3.972" width="13.723" x="7.22" y="12.21" />
    </g>
  </svg>
);
